import { useMemo } from 'react';
import * as S from './styles';
import { formatCurrency } from '@/shared';
import { ClientProps, TaxSubstitutionProps } from '@/types';
import { HandlePrices } from '@/shared/calculatePrices';
import { useCartTotals } from '@/hooks';

interface Props {
  precoSt?: number; // ST base vindo do backend (geralmente por pack)
  precoCalculado?: number; // preço base para cálculo do ST
  quantidade?: number; // quantidade do produto no carrinho (número de packs)
  cliente?: ClientProps;
  extraPadding?: boolean;
  color?: string;
  weight?: number;
  codTributacaoProduto?: number;
  precoPauta?: number;
  listaTributacao?: TaxSubstitutionProps[];
  descontoFlex?: number; // percentual de flex (+ ou -)
  /** qtd de unidades por embalagem (pack) */
  qtdPorEmbalagem?: number;
  /** o que renderizar: total, unitário ou ambos */
  mode?: 'total' | 'unit' | 'both';
}

export const StPrice = ({
  precoSt,
  precoCalculado,
  quantidade = 1,
  cliente,
  extraPadding,
  color,
  weight,
  codTributacaoProduto,
  precoPauta = 0,
  listaTributacao,
  descontoFlex,
  qtdPorEmbalagem,
  mode = 'total',
}: Props) => {
  const { listaTributacao: listaTribGlobal } = useCartTotals();

  const { stTotalFormatted, stUnitFormatted, hasSt } = useMemo(() => {
    const tribList = listaTributacao || listaTribGlobal;

    // quantidade de packs (linha do carrinho / tela)
    const qtyPacks = quantidade && quantidade > 0 ? quantidade : 1;

    let stTotalValue = 0;

    // 1) Se tiver flex + informações completas de tributação, recalcula ST em cima do preço com flex
    if (
      typeof descontoFlex === 'number' &&
      descontoFlex !== 0 &&
      cliente &&
      codTributacaoProduto &&
      precoCalculado &&
      tribList?.length
    ) {
      const precoBase = precoCalculado;
      const precoComFlex = precoBase + (precoBase / 100) * descontoFlex;

      const tributacao = HandlePrices.getTributacao(cliente, codTributacaoProduto, tribList);

      const valorBaseLinha = precoComFlex * qtyPacks;
      const calculado = HandlePrices.CalculaST(tributacao, valorBaseLinha, precoPauta);

      stTotalValue = calculado || precoSt || 0;
    } else {
      // 2) Sem flex (ou sem dados completos), replica tua lógica antiga
      if (!quantidade || quantidade <= 1) {
        // 1 pack → usa precoSt direto
        stTotalValue = precoSt || 0;
      } else if (cliente && codTributacaoProduto && precoCalculado && tribList?.length) {
        const tributacao = HandlePrices.getTributacao(cliente, codTributacaoProduto, tribList);

        const calculado = HandlePrices.CalculaST(tributacao, precoCalculado * quantidade, precoPauta);

        stTotalValue = calculado || precoSt || 0;
      } else {
        // fallback
        stTotalValue = precoSt || 0;
      }
    }

    const stTotalFormatted = formatCurrency({
      value: stTotalValue || 0,
    });

    let stUnitFormatted: string | number = '';

    if (qtdPorEmbalagem && qtdPorEmbalagem > 0 && qtyPacks > 0) {
      const totalUnidades = qtdPorEmbalagem * qtyPacks;
      const stUnit = totalUnidades > 0 ? stTotalValue / totalUnidades : 0;
      stUnitFormatted = formatCurrency({ value: stUnit || 0 });
    }

    const hasSt = !!precoSt || !!precoCalculado;

    return { stTotalFormatted, stUnitFormatted, hasSt };
  }, [
    descontoFlex,
    quantidade,
    cliente,
    codTributacaoProduto,
    precoCalculado,
    precoPauta,
    precoSt,
    qtdPorEmbalagem,
    listaTributacao,
    listaTribGlobal,
  ]);

  const price = useMemo(() => {
    if (descontoFlex) {
      if (cliente && codTributacaoProduto && precoCalculado && listaTributacao) {
        const precoCalc = precoCalculado + (precoCalculado / 100) * descontoFlex;

        const tributacao = HandlePrices.getTributacao(cliente, codTributacaoProduto, listaTributacao);

        const preco = HandlePrices.CalculaST(tributacao, precoCalc * quantidade, precoPauta);

        return formatCurrency({ value: preco ? preco : precoSt ? precoSt : 0 });
      }
    }
    if (!quantidade || quantidade <= 1) {
      return formatCurrency({ value: precoSt ? precoSt : 0 });
    } else {
      if (cliente && codTributacaoProduto && precoCalculado && listaTributacao) {
        const tributacao = HandlePrices.getTributacao(cliente, codTributacaoProduto, listaTributacao);

        const preco = HandlePrices.CalculaST(tributacao, precoCalculado * quantidade, precoPauta);

        return formatCurrency({ value: preco ? preco : precoSt ? precoSt : 0 });
      }
    }
  }, [descontoFlex, quantidade, cliente, codTributacaoProduto, precoCalculado, listaTributacao, precoPauta, precoSt]);

  return (
    <S.Container $extraPadding={extraPadding}>
      <S.Text $weight={weight} $shouldShow={hasSt}>
        {hasSt ? 'ST' : ''}
      </S.Text>

      {hasSt && (
        <>
          {(mode === 'total' || mode === 'both') && (
            <S.Text $size={10} $weight={400} $color={color || '#828282'}>
              {stTotalFormatted}
            </S.Text>
          )}

          {(mode === 'unit' || mode === 'both') && stUnitFormatted && (
            <S.Text $size={10} $weight={400} $color={color || '#828282'}>
              {stUnitFormatted}
            </S.Text>
          )}
        </>
      )}
    </S.Container>
  );
};

export default StPrice;
